import React, { useCallback, useEffect, useRef, useState } from 'react';
import { XMarkIcon, TableCellsIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { mockImportData } from './data/mockImportData';
import { useImportState } from './hooks/useImportState';
import PageThumbnails from './components/PageThumbnails';
import PdfViewer, { PdfViewerHandle } from './components/PdfViewer';
import ImportForm from './components/ImportForm';
import ClassifyingCells from './components/ClassifyingCells';
import ClassifyingBatchBar from './components/ClassifyingBatchBar';

interface ImportMainProps {
  onClose?: () => void;
}

type SideMode = 'viewer' | 'classifying';

const EXTRA_COLUMN_COUNT = 2; // 1 combined classification cell + 1 status icon
const CLASSIFYING_HIDDEN = new Set(['unite', 'quantite', 'prixUnitaire', 'totalHT']);

const CLASSIFYING_HEADERS = ['Lot', 'Ensemble', 'Sous-ens.'];

const extraHeaders = (
  <>
    <th className="w-[40%] py-2 pl-3 pr-1">
      <div className="flex gap-1 text-left text-xs text-gray-500 uppercase tracking-wider">
        {CLASSIFYING_HEADERS.map((h) => (
          <span key={h} className="flex-1">{h}</span>
        ))}
      </div>
    </th>
    <th className="w-6 py-2"></th>
  </>
);

const ImportMain: React.FC<ImportMainProps> = ({ onClose }) => {
  const {
    metadata,
    updateMetadata,
    lines,
    undo,
    redo,
    canUndo,
    canRedo,
    addLine,
    deleteLine,
    updateLine,
    mergeLine,
    splitLine,
    reorderLines,
    modifiedFields,
    mergeError,
    dismissMergeError,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    updateClassification,
    batchClassify,
    clearClassification,
    validateImport,
    pdfNav,
    highlight,
    hoverLine,
    clickLine,
    scrollToLine,
    registerLineRef,
    pdfUrl,
    totalPages,
  } = useImportState(mockImportData);

  const pdfViewerRef = useRef<PdfViewerHandle>(null);
  const [sideMode, setSideMode] = useState<SideMode>('viewer');

  // Thumbnail click → update state + scroll viewer
  const handleGoToPage = useCallback((page: number) => {
    pdfNav.goToPage(page);
    pdfViewerRef.current?.scrollToPage(page);
  }, [pdfNav]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if (
        ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) ||
        ((e.metaKey || e.ctrlKey) && e.key === 'y')
      ) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  // Single fire-and-forget: scroll PDF + table, then let go
  const handleClickLine = useCallback((id: string | null) => {
    clickLine(id);
    if (id) {
      scrollToLine(id);
      const line = lines.find((l) => l.id === id);
      if (line?.pdfRegion) {
        pdfNav.goToPage(line.pdfRegion.page);
        pdfViewerRef.current?.scrollToPage(line.pdfRegion.page);
      }
    }
  }, [clickLine, scrollToLine, lines, pdfNav]);

  // Render extra cells for classifying mode
  const renderExtraCells = useCallback(
    (line: typeof lines[number]) => (
      <ClassifyingCells
        line={line}
        selectedIds={selectedIds}
        onUpdateClassification={updateClassification}
        onBatchClassify={batchClassify}
      />
    ),
    [selectedIds, updateClassification, batchClassify]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="flex flex-col bg-white rounded-xl shadow-2xl w-[calc(100vw-40px)] h-[calc(100vh-40px)] overflow-hidden">
        {/* Title bar */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-semibold text-gray-800">Import de devis</h1>
            <span className="text-xs text-gray-400">{metadata.reference}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle viewer ↔ classifying */}
            <button
              onClick={() => setSideMode((m) => (m === 'viewer' ? 'classifying' : 'viewer'))}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors border border-gray-200"
            >
              {sideMode === 'viewer' ? (
                <>
                  <TableCellsIcon className="w-3.5 h-3.5" />
                  <span>Catégoriser</span>
                </>
              ) : (
                <>
                  <DocumentIcon className="w-3.5 h-3.5" />
                  <span>Voir le devis</span>
                </>
              )}
            </button>

            <button
              onClick={validateImport}
              className="px-3 py-1.5 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              Valider l'import
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 min-h-0">
          {sideMode === 'viewer' ? (
            <>
              {/* Col 1: Thumbnails */}
              <div className="w-24 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-auto">
                <PageThumbnails
                  pdfUrl={pdfUrl}
                  totalPages={totalPages}
                  currentPage={pdfNav.currentPage}
                  onGoToPage={handleGoToPage}
                />
              </div>

              {/* Col 2: PDF Viewer */}
              <div className="flex-[4] min-w-0 border-r border-gray-200">
                <PdfViewer
                  ref={pdfViewerRef}
                  pdfUrl={pdfUrl}
                  currentPage={pdfNav.currentPage}
                  totalPages={totalPages}
                  scale={pdfNav.scale}
                  rotation={pdfNav.rotation}
                  lines={lines}
                  highlight={highlight}
                  onPrevPage={pdfNav.prevPage}
                  onNextPage={pdfNav.nextPage}
                  onZoomIn={pdfNav.zoomIn}
                  onZoomOut={pdfNav.zoomOut}
                  onResetZoom={pdfNav.resetZoom}
                  onRotate={pdfNav.rotate}
                  onHoverLine={hoverLine}
                  onClickLine={handleClickLine}
                  onGoToPage={pdfNav.goToPage}
                  scrollToLine={scrollToLine}
                />
              </div>

              {/* Col 3: Form */}
              <div className="flex-[6] min-w-0">
                <ImportForm
                  metadata={metadata}
                  onUpdateMetadata={updateMetadata}
                  lines={lines}
                  highlight={highlight}
                  modifiedFields={modifiedFields}
                  mergeError={mergeError}
                  onDismissMergeError={dismissMergeError}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={undo}
                  onRedo={redo}
                  onUpdateLine={updateLine}
                  onDeleteLine={deleteLine}
                  onMergeLine={mergeLine}
                  onSplitLine={splitLine}
                  onAddLine={addLine}
                  onReorderLines={reorderLines}
                  onHoverLine={hoverLine}
                  onClickLine={handleClickLine}
                  registerLineRef={registerLineRef}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll}
                />
              </div>
            </>
          ) : (
            /* Classifying mode: full-width form with extra columns */
            <div className="flex-1 min-w-0">
              <ImportForm
                metadata={metadata}
                onUpdateMetadata={updateMetadata}
                lines={lines}
                highlight={highlight}
                modifiedFields={modifiedFields}
                mergeError={mergeError}
                onDismissMergeError={dismissMergeError}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={undo}
                onRedo={redo}
                onUpdateLine={updateLine}
                onDeleteLine={deleteLine}
                onMergeLine={mergeLine}
                onSplitLine={splitLine}
                onAddLine={addLine}
                onReorderLines={reorderLines}
                onHoverLine={hoverLine}
                onClickLine={handleClickLine}
                registerLineRef={registerLineRef}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                extraHeaders={extraHeaders}
                renderExtraCells={renderExtraCells}
                extraColumnCount={EXTRA_COLUMN_COUNT}
                hiddenColumns={CLASSIFYING_HIDDEN}
                toolbarSlot={
                  <ClassifyingBatchBar
                    selectedIds={selectedIds}
                    lines={lines}
                    onBatchClassify={batchClassify}
                    onClearClassification={clearClassification}
                    onClickLine={handleClickLine}
                  />
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportMain;
